import type React from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { isPresent } from "@apl-digital/utils";

import { TheatreResource } from "@/api/controller/theatreResource";
import GenericModal from "@/base/components/Global/GenericModal";
import { useAppDispatch } from "@/store";
import { getTheatreResource } from "@/store/theatreResource";

import QrCodeScannerModalContent, {
  type QrCodeScannerModalContentProps,
} from "./QrCodeScannerModalContent";

const useStyles = createUseStyles(({ color }) => ({
  outerContainer: {
    background: color.modalQrScannerOuterContainerBg,
  },
}));

type TheatreResourceScannerModalProps = {
  isOpen: boolean;
  onAdded?: (value: string) => void;
  onClose: () => void;
  switchToManual?: () => void;
};

export const TheatreResourceScannerModal: React.FC<
  TheatreResourceScannerModalProps
> = ({ isOpen, onAdded, onClose, switchToManual }) => {
  const classes = useStyles();
  const { t } = useTranslation("global");
  const dispatch = useAppDispatch();

  const validateQrCode: QrCodeScannerModalContentProps["onValidate"] = async (
    value: string,
  ) => {
    let scanData = value;

    while (scanData && scanData.length > 0 && !scanData.startsWith("http")) {
      scanData = scanData.slice(1);
    }

    const url: URL = new URL(scanData);
    const currentHost = globalThis.location.host;

    if (currentHost != url.host) {
      return {
        isValid: false,
        message: t("TheatreResourceScannerModal.errorMessages.invalidUrl"),
      } as const;
    }

    const tableCode = url.pathname.split("/").at(2);

    if (!isPresent(tableCode)) {
      console.error("Failed to read table code from URL", url);

      return {
        isValid: false,
        message: t(
          "TheatreResourceScannerModal.errorMessages.failedToParseTheatreResourceCode",
        ),
      } as const;
    }

    return TheatreResource.getByBarcode({
      barcodeWithoutChecksum: tableCode,
    })
      .then((response) => {
        if (response.isResponseOk) {
          return {
            isValid: true,
            value: tableCode,
          } as const;
        }

        return {
          isValid: false,
          message: t(
            "TheatreResourceScannerModal.errorMessages.theatreResourceNotFound",
          ),
        } as const;
      })
      .catch(() => {
        return {
          isValid: false,
          message: t(
            "TheatreResourceScannerModal.errorMessages.theatreResourceNotFound",
          ),
        } as const;
      });
  };

  const onScanned: QrCodeScannerModalContentProps["onScanned"] = (value) => {
    void dispatch(getTheatreResource({ barcodeWithoutChecksum: value }))
      .unwrap()
      .then(() => {
        onAdded?.(value);
      })
      .catch(() => {
        // Validation already accepted this value; keep it available for retry.
        onAdded?.(value);
      });
  };

  return (
    <GenericModal
      customZIndex={98}
      isOpen={isOpen}
      closeModal={onClose}
      isFullWidth
      override={classes.outerContainer}
    >
      <QrCodeScannerModalContent
        onValidate={validateQrCode}
        onScanned={onScanned}
        onClose={onClose}
        switchToManual={switchToManual}
      />
    </GenericModal>
  );
};
